<template>
  <Transition name="fade" mode="out-in">
    <!-- CONTENT -->

    <div v-if="!isLoading && isRenderData" key="content" class="flex flex-col">
      <HeroSectionTitle :getModule="module" :getSection="sectionName" :getSectionId="id" />
      <section class="py-8">
        <component
          :is="currentComponent"
          v-if="currentComponent"
          :passSectionId="id"
          :passSectionItemPost="sectionItemPost"
          :passSectionItemCalendarPost="sectionItemCalendarPost"
        />
        <div v-else>Blank Page</div>
      </section>
    </div>

    <!-- LOADING -->
    <div v-else key="loader" class="flex justify-center items-center">
      <LoadingGlobal class="h-8 w-8" />
    </div>
  </Transition>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import HeroSectionTitle from '@/components/common/HeroSectionTitle.vue';
import GeneralSection from '@/components/partials/section/announcement/GeneralSection.vue';
import SystemAdvisorySection from '@/components/partials/section/announcement/SysteAdvisorySection.vue';
import CalendarEventSection from '@/components/partials/section/event-engagement/CalendarEventSection.vue';
import CelebrationMilestoneSection from '@/components/partials/section/event-engagement/CelebrationMilestoneSection.vue';
import HolidayOfficeClosureSection from '@/components/partials/section/event-engagement/HolidayOfficeClosureSection.vue';
import CompanyProfileSection from '@/components/partials/section/company-overview/CompanyProfileSection.vue';
import MissionVisionValueSection from '@/components/partials/section/company-overview/MissionVisionValue.vue';
import EmployeeMasterListSection from '@/components/partials/section/employee-overview/EmployeeMasterlist.vue';
import OfficeMemoSection from '@/components/partials/section/document-directories/OfficeMemoSection.vue';
import CompanyPolicySection from '@/components/partials/section/document-directories/CompanyPolicySection.vue';
import EmployeeResponsibilitiesSection from '@/components/partials/section/document-directories/EmployeesResponsibilities.vue';
import HMOSection from '@/components/partials/section/products-policy/HMOSection.vue';
import ProcessAvailmentSection from '@/components/partials/section/products-policy/ProcessAvailment.vue';
import useDecryptParam from '@/utils/decrypt';

const componentMap = {
  'general-updates': GeneralSection,
  'system-advisories': SystemAdvisorySection,
  'calendar-event': CalendarEventSection,
  'celebration-milestone': CelebrationMilestoneSection,
  'holiday-office-closure': HolidayOfficeClosureSection,
  'company-profile': CompanyProfileSection,
  'mission-vision': MissionVisionValueSection,
  'employee-masterlist': EmployeeMasterListSection,
  'general-memorandum': OfficeMemoSection,
  'company-policy': CompanyPolicySection,
  'employee-duty-responsibility': EmployeeResponsibilitiesSection,
  'process-availment': ProcessAvailmentSection,
  hmo: HMOSection
};

export default {
  components: { HeroSectionTitle },
  setup() {
    const axios = inject('$axios');
    const route = useRoute();

    const module = computed(() => route.params.module);
    const section = computed(() => route.params.section);

    const { decryptedValue: id } = useDecryptParam('id');
    const isLoading = ref(true);
    const isRenderData = ref(false);
    const sectionName = ref('');
    const sectionItemPost = ref([]);
    const sectionItemCalendarPost = ref([]);

    const currentComponent = computed(() => componentMap[section.value] || null);

    const fetchSection = async () => {
      isLoading.value = true;
      isRenderData.value = false;
      try {
        const { data } = await axios.get(`/api/module-sections/${id.value}`, {
          params: {
            status: 'published'
          }
        });
        sectionItemPost.value = data.data.modulePosts;
        sectionItemCalendarPost.value = data.data.calendarEvents;
        sectionName.value = data.data.name;
        isRenderData.value = true;
      } catch (error) {
        console.error('render error:', error.response || error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      fetchSection();
    });
    watch(id, () => {
      fetchSection();
    });

    return {
      isLoading,
      isRenderData,
      module,
      section,
      id,
      currentComponent,
      sectionName,
      sectionItemPost,
      sectionItemCalendarPost
    };
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
